import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Mail, CheckCircle, AlertCircle, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'react-toastify';

const EmailVerification = ({ onBackToLogin, userEmail, onVerified }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, resend
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      // Sem token na URL: preparar modo de reenvio
      if (userEmail) setEmail(userEmail);
      setStatus('resend');
    }
  }, [token, navigate, userEmail]);

  const verifyEmail = async (verificationToken) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/auth/verify-email', {
        token: verificationToken
      });

      if (response.data.token) {
        // Fazer login automático após verificação usando o contexto
        await login(response.data.token, response.data.user);
        setStatus('success');
        toast.success('Email verificado com sucesso!');
        if (onVerified) onVerified();
        
        // Redirecionar imediatamente para o sistema
        setTimeout(() => {
          navigate('/home');
        }, 1500);
      }
    } catch (error) {
      console.error('Erro na verificação:', error);
      setStatus('error');
      toast.error(error.response?.data?.message || 'Erro ao verificar email');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!email) {
      toast.error('Por favor, digite seu email');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/auth/resend-verification', { email });
      toast.success('Email de verificação reenviado!');
      setStatus('sent');
    } catch (error) {
      console.error('Erro ao reenviar:', error);
      toast.error(error.response?.data?.message || 'Erro ao reenviar email');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verificando email...</h2>
            <p className="text-gray-600">Aguarde enquanto confirmamos seu endereço de email.</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Email verificado!</h2>
            <p className="text-gray-600 mb-4">
              Seu email foi verificado com sucesso. Você será redirecionado automaticamente.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 text-sm">
                ✅ Agora você tem acesso completo ao sistema!
              </p>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Erro na verificação</h2>
            <p className="text-gray-600 mb-6">
              O link de verificação pode ter expirado ou ser inválido.
            </p>
            <div className="space-y-4">
              <div className="form-group">
                <label className="form-label">Digite seu email para reenviar:</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <button
                onClick={resendVerification}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                Reenviar verificação
              </button>
            </div>
          </div>
        );

      case 'resend':
      case 'sent':
        return (
          <div className="text-center">
            <Mail className="h-16 w-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {status === 'sent' ? 'Email enviado!' : 'Verificar email'}
            </h2>
            <p className="text-gray-600 mb-6">
              {status === 'sent' 
                ? 'Verifique sua caixa de entrada e clique no link de verificação.'
                : 'Digite seu email para receber o link de verificação.'}
            </p>
            
            {status === 'resend' && (
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">Email:</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <button
                  onClick={resendVerification}
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  Enviar verificação
                </button>
              </div>
            )}
            
            {status === 'sent' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <div className="flex items-center gap-2 text-blue-800 text-sm">
                  <Clock className="h-4 w-4" />
                  <span>O link expira em 24 horas</span>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-3">
              <img src="/apas.png" alt="Logo Apascentar" style={{ width: '100px', height: '100px' }} />
            </div>
            <h1 className="text-3xl font-bold text-blue-600 mb-2">Igreja Apascentar</h1>
            <p className="text-gray-500">Sistema de Gestão</p>
          </div>
          
          {renderContent()}
          
          <div className="mt-8 text-center">
            <button
              onClick={() => onBackToLogin ? onBackToLogin() : navigate('/login')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              ← Voltar ao login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;